import type { PropType } from 'vue';
import type { TSelectProps } from './TSelect.types';
import { Icon } from '@iconify/vue';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, ref, watch } from 'vue';
import { useScrollbarThumb } from '../../composables/scrollbarThumb';
import { useTheme } from '../../composables/theme';
import { useStyles } from './TSelect.styles';
import {
  buildSelectOptions,
  getLeftIconName,
  isOptionValueEqual,
  resolveLeftIcon,
} from './TSelect.utils';
import { TSelectOption } from './TSelectOption';
import { useSelectDropdown } from './useSelectDropdown';

export const TSelect = defineComponent({
  name: 'TSelect',
  emits: ['input', 'change', 'update:modelValue', 'focus', 'blur'],
  props: {
    error: Boolean as PropType<TSelectProps['error']>,
    disabled: Boolean as PropType<TSelectProps['disabled']>,
    leftIcon: String as PropType<TSelectProps['leftIcon']>,
    label: String as PropType<TSelectProps['label']>,
    placeholder: String as PropType<TSelectProps['placeholder']>,
    helperText: String as PropType<TSelectProps['helperText']>,
    value: {
      type: [
        String,
        Number,
        Boolean,
        Object,
        Array,
        Function,
        Date,
      ] as PropType<TSelectProps['value']>,
      default: null,
    },
    modelValue: {
      type: [
        String,
        Number,
        Boolean,
        Object,
        Array,
        Function,
        Date,
      ] as PropType<TSelectProps['modelValue']>,
      default: null,
    },
    fullWidth: Boolean as PropType<TSelectProps['fullWidth']>,
    id: String as PropType<TSelectProps['id']>,
  },
  setup(_props, { attrs, emit, slots, expose }) {
    const props = computed(() => merge({}, _props, attrs));
    const theme = useTheme();
    const localValue = ref<TSelectProps['value']>(null);

    const baseClass = useStyles();
    const containerClasses = computed(() => [
      baseClass.value,
      `${baseClass.value}__container`,
      {
        [`${baseClass.value}--error`]: props.value.error,
        [`${baseClass.value}--full-width`]: props.value.fullWidth,
      },
    ]);

    watch(
      () => [props.value.modelValue, props.value.value],
      ([modelValue, value]) => {
        localValue.value = modelValue !== null ? modelValue : value;
      },
      { immediate: true },
    );

    const internalValue = computed({
      get: () => {
        if (props.value.modelValue !== null) {
          return props.value.modelValue;
        }
        if (props.value.value !== null) {
          return props.value.value;
        }
        return localValue.value;
      },
      set: (newValue) => {
        localValue.value = newValue;
        emit('update:modelValue', newValue);
        emit('input', newValue);
        emit('change', newValue);
      },
    });

    const options = computed(() => buildSelectOptions(slots.default?.() ?? []));

    const selectedOption = computed(() => options.value.find((opt) => isOptionValueEqual(opt.value, internalValue.value)));
    const hasSelectedValue = computed(() => Boolean(selectedOption.value));
    const selectedLabel = computed(() => {
      if (selectedOption.value) {
        return selectedOption.value.label;
      }
      if (props.value.placeholder) {
        return props.value.placeholder;
      }
      return '';
    });

    const resolvedLeftIcon = computed(() => resolveLeftIcon(props.value as Record<string, unknown>));
    const leftIconName = computed(() => getLeftIconName(resolvedLeftIcon.value, theme.value.iconSet));

    const dropdown = useSelectDropdown(
      options,
      internalValue,
      computed(() => Boolean(props.value.disabled)),
    );

    const scrollbar = useScrollbarThumb(dropdown.menuRef, {
      onDragStart: () => {
        dropdown.isInteractingWithMenu.value = true;
      },
      onDragEnd: () => {
        requestAnimationFrame(() => {
          dropdown.isInteractingWithMenu.value = false;
        });
      },
    });

    watch(
      () => [dropdown.menuOpen.value, options.value.length] as const,
      ([isOpen]) => {
        if (isOpen) {
          requestAnimationFrame(() => scrollbar.update());
        }
      },
    );

    const focus = () => dropdown.triggerRef.value?.focus();
    expose({ focus });

    return () => (
      <div class={containerClasses.value}>
        {slots.label?.()
        || (props.value.label && (
          <label class={`${baseClass.value}__label`} for={props.value.id}>
            {props.value.label}
          </label>
        ))}
        <div class={`${baseClass.value}__wrapper`}>
          {resolvedLeftIcon.value && (
            <Icon
              icon={leftIconName.value}
              class={[
                `${baseClass.value}__icon`,
                `${baseClass.value}__icon--left`,
              ]}
            />
          )}
          <div
            class={[
              `${baseClass.value}__dropdown`,
              {
                [`${baseClass.value}__dropdown--open`]: dropdown.menuOpen.value,
              },
            ]}
            ref={dropdown.wrapperRef}
          >
            <button
              class={[
                `${baseClass.value}__select`,
                {
                  [`${baseClass.value}__select--left-icon`]:
                    resolvedLeftIcon.value,
                  [`${baseClass.value}__select--placeholder`]:
                    !hasSelectedValue.value,
                  [`${baseClass.value}__select--open`]: dropdown.menuOpen.value,
                },
              ]}
              disabled={props.value.disabled}
              id={props.value.id}
              onClick={dropdown.handleClickTrigger}
              onFocus={(e: Event) => emit('focus', e)}
              onBlur={(e: Event) => {
                dropdown.handleBlur();
                emit('blur', e);
              }}
              onKeydown={dropdown.handleKeydown}
              ref={dropdown.triggerRef}
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={dropdown.menuOpen.value}
              type="button"
              {...attrs}
            >
              <div class={`${baseClass.value}__selected-value`}>
                {selectedLabel.value}
              </div>
            </button>

            {dropdown.menuOpen.value && (
              <div
                class={`${baseClass.value}__menu-wrapper`}
                onMousedown={dropdown.handleMenuInteractionStart}
                onMouseup={() => {
                  if (!scrollbar.isDragging.value) {
                    dropdown.isInteractingWithMenu.value = false;
                  }
                }}
                onMouseenter={() => (dropdown.menuHovered.value = true)}
                onMouseleave={() => {
                  dropdown.menuHovered.value = false;
                  if (!scrollbar.isDragging.value) {
                    dropdown.isInteractingWithMenu.value = false;
                  }
                }}
              >
                <ul
                  class={`${baseClass.value}__menu`}
                  role="listbox"
                  ref={dropdown.menuRef}
                  onScroll={scrollbar.update}
                >
                  {options.value.map((option, index) => (
                    <TSelectOption
                      key={`${String(option.value)}-${index}`}
                      label={option.label}
                      selected={isOptionValueEqual(option.value, internalValue.value)}
                      highlighted={index === dropdown.highlightedIndex.value}
                      disabled={option.disabled}
                      withSeparator={index < options.value.length - 1}
                      onHover={() => (dropdown.highlightedIndex.value = index)}
                      onSelect={() => dropdown.selectOption(option)}
                    />
                  ))}
                </ul>
                <div
                  class={[
                    `${baseClass.value}__menu-scrollbar`,
                    {
                      [`${baseClass.value}__menu-scrollbar--visible`]:
                        dropdown.menuHovered.value
                        && scrollbar.isScrollable.value,
                    },
                  ]}
                  aria-hidden="true"
                >
                  <div
                    class={`${baseClass.value}__menu-scrollbar-thumb`}
                    onMousedown={scrollbar.onThumbMouseDown}
                    style={{
                      height: `${scrollbar.thumbHeight.value}px`,
                      transform: `translateY(${scrollbar.thumbOffset.value}px)`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {slots.helperText?.()
        || (props.value.helperText && (
          <p class={`${baseClass.value}__helper-text`}>
            {props.value.helperText}
          </p>
        ))}
      </div>
    );
  },
});
